import React, { useEffect, useState } from "react";
import styled from "styled-components";

const Overlay = styled.div`
  position: fixed; inset: 0;
  background: rgba(15, 23, 42, 0.5);
  z-index: 1000;
  display: flex; align-items: center; justify-content: center;
`;

const Modal = styled.div`
  background: #fff;
  border-radius: 14px;
  width: 420px;
  max-width: 92vw;
  padding: 22px 24px 20px;
  box-shadow: 0 20px 60px rgba(15, 23, 42, 0.25);
`;

const Title = styled.h3`
  margin: 0 0 6px;
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
`;

const Sub = styled.p`
  margin: 0 0 18px;
  font-size: 12px;
  color: #64748b;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 120px 1fr;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;

const Label = styled.label`
  font-size: 13px;
  color: #1e293b;
  font-weight: 600;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 10px;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  font-size: 13px;
  &:focus { border-color: #3f88a5; outline: none; }
`;

const Actions = styled.div`
  display: flex; gap: 10px; justify-content: flex-end;
  margin-top: 18px;
`;

const Btn = styled.button`
  padding: 8px 16px;
  border-radius: 9px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: 1.5px solid ${(p) => (p.$primary ? "#3f88a5" : "#cbd5e1")};
  background: ${(p) => (p.$primary ? "#3f88a5" : "#fff")};
  color: ${(p) => (p.$primary ? "#fff" : "#475569")};
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const ErrMsg = styled.div`
  color: #b91c1c;
  font-size: 12px;
  margin-top: 6px;
`;

const PERIOD_FIELDS = [
  { key: "monthly",    label: "Monthly" },
  { key: "quarterly",  label: "Quarterly" },
  { key: "halfYearly", label: "Half-Yearly" },
  { key: "yearly",     label: "Yearly" },
];

const TriggerBreachScheduleModal = ({ open, onClose, hook }) => {
  const { getBreachSchedule, saveBreachSchedule, selectedFinancialYear } = hook;
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    setLoading(true);
    (async () => {
      const schedules = await getBreachSchedule();
      const next = {};
      for (const { key } of PERIOD_FIELDS) {
        const v = schedules?.[key]?.daysAfter;
        next[key] = v != null ? String(v) : "";
      }
      setValues(next);
      setLoading(false);
    })();
  }, [open, getBreachSchedule]);

  const handleChange = (key, raw) => {
    setValues((v) => ({ ...v, [key]: raw.replace(/[^\d]/g, "") }));
  };

  const handleSave = async () => {
    setError("");
    const schedules = {};
    for (const { key } of PERIOD_FIELDS) {
      const v = values[key];
      if (v === "" || v == null) continue;
      const n = Number(v);
      if (!Number.isFinite(n) || n < 0) {
        setError("Days must be a non-negative number.");
        return;
      }
      schedules[key] = { daysAfter: n };
    }
    setSaving(true);
    const ok = await saveBreachSchedule(schedules);
    setSaving(false);
    if (ok) onClose();
    else setError("Failed to save schedule.");
  };

  if (!open) return null;
  return (
    <Overlay onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <Modal>
        <Title>Breach Email Schedule</Title>
        <Sub>
          For each period type, set how many days after the period ends a breach
          alert email should be sent. Leave blank to disable that period type.
          {selectedFinancialYear ? "" : " Select a financial year first."}
        </Sub>
        {loading ? (
          <div style={{ padding: "20px 0", fontSize: 13, color: "#64748b" }}>Loading…</div>
        ) : (
          <>
            {PERIOD_FIELDS.map(({ key, label }) => (
              <Row key={key}>
                <Label htmlFor={`tbs-${key}`}>{label}</Label>
                <Input
                  id={`tbs-${key}`}
                  type="text"
                  inputMode="numeric"
                  placeholder="Days after period end"
                  value={values[key] ?? ""}
                  onChange={(e) => handleChange(key, e.target.value)}
                />
              </Row>
            ))}
            {error && <ErrMsg>{error}</ErrMsg>}
            <Actions>
              <Btn onClick={onClose} disabled={saving}>Cancel</Btn>
              <Btn $primary onClick={handleSave} disabled={saving || !selectedFinancialYear}>
                {saving ? "Saving…" : "Save"}
              </Btn>
            </Actions>
          </>
        )}
      </Modal>
    </Overlay>
  );
};

export default TriggerBreachScheduleModal;
